/**
 * "Sigue donde lo dejaste": productos vistos recientemente.
 * Se guardan en el navegador del cliente (localStorage) SOLO si ha aceptado las cookies de preferencias
 * (API de privacidad de Shopify). Nunca salen del navegador.
 */

const CLAVE = 'mb:vistos';
const MAXIMO = 8;

/** @returns {Promise<boolean>} si el cliente permite guardar preferencias */
function permitePreferencias() {
  return new Promise((resolve) => {
    const shopify = window.Shopify;
    if (!shopify?.loadFeatures) {
      resolve(false);
      return;
    }
    shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (error) {
        resolve(false);
        return;
      }
      resolve(Boolean(shopify.customerPrivacy?.preferencesProcessingAllowed?.()));
    });
  });
}

/** @returns {string[]} handles de productos vistos, del más reciente al más antiguo */
export function leerVistos() {
  try {
    const lista = JSON.parse(localStorage.getItem(CLAVE) ?? '[]');
    return Array.isArray(lista) ? lista.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string} handle */
export async function registrarVisto(handle) {
  if (!handle || !(await permitePreferencias())) return;
  const lista = [handle, ...leerVistos().filter((h) => h !== handle)].slice(0, MAXIMO);
  try {
    localStorage.setItem(CLAVE, JSON.stringify(lista));
  } catch {
    // Almacenamiento bloqueado o lleno: no pasa nada, la sección simplemente no se muestra.
  }
}

const euros = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' });

class MbVistos extends HTMLElement {
  #controller = new AbortController();

  async connectedCallback() {
    const excluir = this.dataset.excluir ?? '';
    const handles = leerVistos().filter((h) => h !== excluir).slice(0, 4);
    const lista = this.querySelector('[data-mb-vistos-lista]');
    const aviso = this.querySelector('[data-mb-vistos-carrito]');

    const [productos, carrito] = await Promise.all([this.#cargarProductos(handles), this.#cargarCarrito()]);

    if (aviso && carrito?.item_count > 0) {
      const texto = aviso.querySelector('[data-mb-vistos-carrito-texto]');
      if (texto) {
        const unidades = carrito.item_count === 1 ? '1 producto' : `${carrito.item_count} productos`;
        texto.textContent = `Tienes ${unidades} en tu carrito · ${euros.format(carrito.total_price / 100)}`;
      }
      aviso.hidden = false;
    }

    if (lista && productos.length > 0) {
      for (const producto of productos) lista.append(this.#tarjeta(producto));
      lista.hidden = false;
    }

    if (productos.length > 0 || carrito?.item_count > 0) this.hidden = false;
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {string[]} handles */
  async #cargarProductos(handles) {
    const respuestas = await Promise.all(
      handles.map(async (handle) => {
        try {
          const url = new URL(`${window.Shopify?.routes?.root ?? '/'}products/${encodeURIComponent(handle)}.js`, location.origin);
          const r = await fetch(url, { signal: this.#controller.signal });
          return r.ok ? await r.json() : null;
        } catch {
          return null;
        }
      })
    );
    return respuestas.filter((p) => p && p.available);
  }

  async #cargarCarrito() {
    try {
      const url = new URL(`${window.Shopify?.routes?.root ?? '/'}cart.js`, location.origin);
      const r = await fetch(url, { signal: this.#controller.signal });
      return r.ok ? await r.json() : null;
    } catch {
      return null;
    }
  }

  /** @param {{title: string, url: string, price: number, featured_image: string | null}} producto */
  #tarjeta(producto) {
    const item = document.createElement('li');
    item.className = 'mb-vistos__item';
    const enlace = document.createElement('a');
    enlace.className = 'mb-vistos__enlace';
    enlace.href = producto.url;
    if (producto.featured_image) {
      const img = document.createElement('img');
      const src = new URL(producto.featured_image, location.origin);
      src.searchParams.set('width', '300');
      img.src = src.toString();
      img.alt = '';
      img.width = 300;
      img.height = 300;
      img.loading = 'lazy';
      img.className = 'mb-vistos__imagen';
      enlace.append(img);
    }
    const titulo = document.createElement('span');
    titulo.className = 'mb-vistos__titulo';
    titulo.textContent = producto.title;
    const precio = document.createElement('span');
    precio.className = 'mb-vistos__precio';
    precio.textContent = euros.format(producto.price / 100);
    enlace.append(titulo, precio);
    item.append(enlace);
    return item;
  }
}

if (!customElements.get('mb-vistos')) customElements.define('mb-vistos', MbVistos);
